import { ExpertSelect } from "@/components/Expert/ExpertSelect";
import { FilterDropdown } from "@/components/Filter/FilterDropdown";
import { applyFilters, useFilter } from "@/components/Filter/useFilter";
import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { KitDeletionDialog } from "@/components/dependency-deletion/KitDeletionDialog";
import { UserDisplayName } from "@/components/display/UserDisplayName";
import { KitDisplayName } from "@/components/display/display-name/KitDisplayName";
import { Column, Row } from "@/components/ui/flex";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useUserUuid } from "@/hooks/use-user-uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faPlus,
  faTrash,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useCreateInterviewAction } from "../action/use-create-interview-action";
import {
  GroupedTable,
  GroupedTableColumn,
} from "../components/GroupedTable/GroupedTable";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { useGroupExpertsData } from "../data/use-group-experts-data";
import { useKitsData } from "../data/use-kits-data";
import { useScriptsData } from "../data/use-scripts-data";
import { useSettingData } from "../data/use-settings-data";
import { useUsersData } from "../data/use-users-data";
import { useGroupSlug } from "../hooks/use-group-slug";
import { groupByDate, smartDate } from "../lib/date";
import { GetKitDto } from "../lib/gen-api";

export const InterviewsPage = () => {
  const groupUuid = useGroupUuid();
  const groupSlug = useGroupSlug();
  const { value: interviewerFeatureFlag } = useSettingData(
    groupUuid,
    "FEATURE_FLAG_INTERVIEWER",
  );
  const currentUserUuid = useUserUuid();
  const navigate = useNavigate();
  const { kits } = useKitsData(groupUuid, {
    type: "interview",
  });
  const { getUserForUserUuid } = useUsersData(groupUuid);
  const { experts } = useGroupExpertsData(groupUuid);
  const { scripts, isLoading: isLoadingScripts } = useScriptsData(groupUuid);
  const { create, isCreatingInterview } = useCreateInterviewAction();
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [interviewName, setInterviewName] = useState("");
  const [interviewTopic, setInterviewTopic] = useState("");
  const [selectedExpertUuid, setSelectedExpertUuid] = useState<string | null>(
    null,
  );
  const [selectedScriptUuid, setSelectedScriptUuid] = useState<string>("");
  const [selectedAssignedUser, setSelectedAssignedUser] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (interviewerFeatureFlag !== "enabled") {
      setOpenCreateDialog(false);
    }
  }, [interviewerFeatureFlag]);

  const {
    selected: selectedUser,
    setSelected: setSelectedUser,
    options: users,
  } = useFilter(kits, "user_uuid");

  // Get unique user UUIDs that have assignments
  const assignedUsers = Array.from(
    new Set(
      kits.flatMap(
        (kit) => kit.user_assignments?.map((a) => a.user_uuid) || [],
      ),
    ),
  );

  const filteredKits = applyFilters(kits, {
    user_uuid: selectedUser,
  }).filter((kit) => {
    // First filter: Only show interviews assigned to or owned by current user
    const isOwner = kit.user_uuid === currentUserUuid;
    const isAssigned = kit.user_assignments?.some(
      (a) => a.user_uuid === currentUserUuid,
    );

    if (!isOwner && !isAssigned) {
      return false;
    }

    // Second filter: Apply "Assigned To" dropdown filter
    if (selectedAssignedUser) {
      return kit.user_assignments?.some(
        (a) => a.user_uuid === selectedAssignedUser,
      );
    }

    return true;
  });

  const groupedKits = groupByDate(filteredKits, "date_updated");

  const columns: GroupedTableColumn<GetKitDto>[] = [
    { key: "name", label: "Name" },
    { key: "date_updated", label: "Last Modified" },
    { key: "user_uuid", label: "Owned By" },
    { key: "user_assignments", label: "Assigned To" },
  ];

  const handleCreateInterview = async () => {
    if (!selectedExpertUuid) {
      toast.error("Please select an expert");
      return;
    }

    if (!interviewName.trim()) {
      return;
    }

    const { kitUuid } = await create({
      expertUuid: selectedExpertUuid,
      name: interviewName,
      scriptUuid: selectedScriptUuid || undefined,
      description: interviewTopic.trim() || undefined,
    });
    setOpenCreateDialog(false);
    setInterviewName("");
    setInterviewTopic("");
    setSelectedExpertUuid(null);
    setSelectedScriptUuid("");
    navigate({
      to: "/w/$groupSlug/k/$kitUuid",
      params: { groupSlug, kitUuid },
    });
  };

  const canCreateInterview =
    interviewerFeatureFlag === "enabled" &&
    experts.some((expert) => expert.expertType === "interviewer");

  return (
    <Page className="bg-background-1">
      <PageHeader>
        <PageHeader.Title>Interviews</PageHeader.Title>
      </PageHeader>
      <PageBody>
        <div>
          {canCreateInterview && (
            <Dialog open={openCreateDialog} onOpenChange={setOpenCreateDialog}>
              <DialogTrigger asChild>
                <Button className="mb-4">
                  <FontAwesomeIcon icon={faPlus} />
                  New Interview
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Interview</DialogTitle>
                  <DialogDescription>
                    Enter a name and select an expert for your interview.
                  </DialogDescription>
                </DialogHeader>
                <Column className="gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Interview Name *</Label>
                    <Input
                      id="name"
                      value={interviewName}
                      onChange={(e) => setInterviewName(e.target.value)}
                      placeholder="e.g., Q4 Performance Review"
                      disabled={isCreatingInterview}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !isCreatingInterview) {
                          handleCreateInterview();
                        }
                      }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="topic">Topic</Label>
                    <Textarea
                      id="topic"
                      placeholder="Describe what this interview is about..."
                      value={interviewTopic}
                      onChange={(e) => setInterviewTopic(e.target.value)}
                      rows={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Interviewer Expert *</Label>
                    <ExpertSelect
                      expertUuid={selectedExpertUuid}
                      setExpertUuid={setSelectedExpertUuid}
                      experts={experts}
                      autoSelect={true}
                      filterByType="interviewer"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="script">Interview Script (Optional)</Label>
                    <Select
                      value={selectedScriptUuid}
                      onValueChange={(value) => {
                        setSelectedScriptUuid(value === "none" ? "" : value);
                      }}
                    >
                      <SelectTrigger id="script">
                        <SelectValue placeholder="No script selected" />
                      </SelectTrigger>
                      <SelectContent>
                        {isLoadingScripts ? (
                          <SelectItem value="loading" disabled>
                            Loading scripts...
                          </SelectItem>
                        ) : scripts.length === 0 ? (
                          <SelectItem value="none" disabled>
                            No scripts found
                          </SelectItem>
                        ) : (
                          scripts.map((script) => (
                            <SelectItem key={script.uuid} value={script.uuid}>
                              {script.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-muted-foreground text-xs">
                      Scripts are optional. Leave unselected to skip.
                    </p>
                  </div>
                </Column>
                <DialogFooter>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setOpenCreateDialog(false);
                      setInterviewName("");
                      setInterviewTopic("");
                      setSelectedExpertUuid(null);
                      setSelectedScriptUuid("");
                    }}
                    disabled={isCreatingInterview}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleCreateInterview}
                    disabled={
                      isCreatingInterview ||
                      !interviewName.trim() ||
                      !selectedExpertUuid
                    }
                  >
                    {isCreatingInterview ? "Creating..." : "Create"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
        <Row className="gap-2">
          <FilterDropdown
            label="Owned By"
            options={users}
            value={selectedUser}
            onChange={setSelectedUser}
            getLabel={(user) => {
              if (user === null) {
                return "All Users";
              }
              const userData = getUserForUserUuid(user);
              if (!userData) return "Unknown User";
              return <UserDisplayName user={userData} />;
            }}
            allLabel="All Users"
            className="border-border"
          />
          <FilterDropdown
            label="Assigned To"
            options={assignedUsers}
            value={selectedAssignedUser}
            onChange={setSelectedAssignedUser}
            getLabel={(user) => {
              if (user === null) {
                return "All Assignments";
              }
              const userData = getUserForUserUuid(user);
              if (!userData) return "Unknown User";
              return <UserDisplayName user={userData} />;
            }}
            allLabel="All Assignments"
            className="border-border"
          />
        </Row>

        <GroupedTable
          isSortable
          isSearchable
          data={groupedKits}
          columns={columns}
          itemIdKey="uuid"
          renderCell={{
            name: (_, row) => <KitDisplayName kit={row} />,
            user_uuid: (value) => <UserCell userUuid={value} />,
            date_updated: (value) => smartDate(value),
            user_assignments: (_, row) => <AssignedToCell kit={row} />,
          }}
          renderActions={({ row }) => <RowActions row={row} />}
          rowLink={(row) => ({
            to: "/w/$groupSlug/k/$kitUuid",
            params: { groupSlug, kitUuid: row.uuid },
          })}
          emptyMessage="Your interviews will show up here. Create your first to kick things off"
        />
      </PageBody>
    </Page>
  );
};

function UserCell({ userUuid }: { userUuid: string | null }) {
  const groupUuid = useGroupUuid();
  const { getUserForUserUuid } = useUsersData(groupUuid);
  if (!userUuid) return <></>;
  const user = getUserForUserUuid(userUuid);
  if (!user) return <></>;
  return <UserDisplayName user={user} />;
}

function AssignedToCell({ kit }: { kit: GetKitDto }) {
  const groupUuid = useGroupUuid();
  const { getUserForUserUuid } = useUsersData(groupUuid);
  const assignments = kit.user_assignments || [];

  if (assignments.length === 0) {
    return (
      <span className="text-muted-foreground text-sm">No assignments</span>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {assignments.map((assignment) => {
        const user = getUserForUserUuid(assignment.user_uuid);
        if (!user) return null;
        return <UserDisplayName user={user} />;
      })}
    </div>
  );
}

function RowActions({ row }: { row: GetKitDto }) {
  const groupUuid = useGroupUuid();
  const { updateKit } = useKitsData(groupUuid);
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState(row.name);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setNewName(row.name);
  }, [row.name]);

  const handleEdit = async () => {
    setLoading(true);
    setError(null);

    try {
      await updateKit({ kitUuid: row.uuid, name: newName });
      setOpen(false);
      toast.success("Interview name updated");
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      setError("Failed to update name");
      toast.error("Failed to update name");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="ghost" size="sm" aria-label="Edit" title="Edit">
            <FontAwesomeIcon icon={faEdit} />
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Interview Name</DialogTitle>
            <DialogDescription>
              Change the name of this interview. This will be visible to all
              group members.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Interview name"
            disabled={loading}
            className="mb-2"
            autoFocus
          />
          {error && <div className="mb-2 text-sm text-red-500">{error}</div>}
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleEdit}
              disabled={loading || !newName.trim()}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <KitDeletionDialog kitUuid={row.uuid} groupUuid={row.group_uuid}>
        <Button
          variant="ghost"
          size="sm"
          className="hover:bg-destructive hover:text-destructive-foreground"
          aria-label="Delete"
          title="Delete"
        >
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      </KitDeletionDialog>
    </>
  );
}
